import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { RoundButton } from '@/components/UI/RoundButton';
import { InputTextField } from '@/components/UI/InputTextField';
import { PhoneNumberField } from '@/components/UI/PhoneNumberField';
import CountrySelectField from '@/components/UI/CountrySelectField';
import { BasicUploadField } from '@/kit/form/BasicUploadField';
import { PDF_ONLY } from '@/helpers/common';
import './index.scss';

const VALIDATION_SCHEMA = t =>
  Yup.object({
    country: Yup.string().required(t('profile_contacts_form.country_is_required')),
    city: Yup.string().required(t('profile_contacts_form.city_is_required')),
    address: Yup.string()
      .max(255, t('profile_contacts_form.address_exceeds_limit'))
      .required(t('profile_contacts_form.address_is_required')),
    postal_code: Yup.string()
      .min(4, t('profile_contacts_form.invalid_postal_code'))
      .max(10, t('profile_contacts_form.invalid_postal_code')),
    phone: Yup.number().required(t('profile_contacts_form.phone_number_is_required')),
    is_valid_phone: Yup.bool()
      .oneOf([true], t('profile_contacts_form.invalid_phone_number'))
      .required(),
    proof_of_residential_address: Yup.array()
      .min(1, t('profile_contacts_form.proof_of_residential_address_required'))
      .required(t('profile_contacts_form.proof_of_residential_address_required')),
  });

const ProfileContactsForm = ({ userLocation, user, onSubmit, onCancel }) => {
  const { t } = useTranslation('components');

  const getInitialValues = (user, location) => ({
    country: (user && user.country) || (location && location.country) || '',
    country_code: (user && user.country_code) || (location && location.countryCode) || '',
    city: (user && user.city) || (location && location.city) || '',
    address: (user && user.address) || '',
    postal_code: (user && user.postal_code) || '',
    phone: (user && user.phone) || '',
    is_valid_phone: true,
    proof_of_residential_address: user?.proof_of_residential_address
      ? [user.proof_of_residential_address]
      : [],
  });

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(user, userLocation)}
      validationSchema={VALIDATION_SCHEMA(t)}
      onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
    >
      {({ values, setFieldValue, setFieldError, errors, touched, handleChange, handleSubmit }) => (
        <form className="profile-personal-form" onSubmit={handleSubmit}>
          <h1 className="profile-personal-form__title f-32 f-500">
            {t('profile_contacts_form.edit_contact_information')}
          </h1>
          <div className="profile-personal-form__container">
            <CountrySelectField
              label={t('profile_contacts_form.residence_country')}
              name="country"
              value={values.country_code}
              onChange={(country, code) => {
                setFieldValue('country', country);
                setFieldValue('country_code', code);
              }}
              error={errors.country && touched.country && errors.country}
              className="profile-personal-form__field"
            />
            <PhoneNumberField
              label={t('profile_contacts_form.phone_number')}
              name="phone"
              value={values.phone}
              countryCode={values.country_code}
              onChange={phone => setFieldValue('phone', phone)}
              onClear={() => setFieldValue('phone', '')}
              error={
                (errors.phone || errors.is_valid_phone) &&
                (touched.phone || touched.is_valid_phone) &&
                (errors.phone || errors.is_valid_phone)
              }
              isValid={values.is_valid_phone}
              formikBag={{ setFieldValue, touched }}
              containerID="phone-input"
              className="profile-personal-form__field"
            />
            <InputTextField
              label={t('profile_contacts_form.residence_city')}
              name="city"
              value={values.city}
              onChange={handleChange}
              onClear={() => setFieldValue('city', '')}
              error={errors.city && touched.city && errors.city}
              className="profile-personal-form__field"
            />
            <InputTextField
              label={t('profile_contacts_form.residence_address')}
              name="address"
              value={values.address}
              onChange={handleChange}
              onClear={() => setFieldValue('address', '')}
              error={errors.address && touched.address && errors.address}
              className="profile-personal-form__field"
            />
            <InputTextField
              label={t('profile_contacts_form.residence_postal_code')}
              name="postal_code"
              value={values.postal_code}
              onChange={handleChange}
              onClear={() => setFieldValue('postal_code', '')}
              error={errors.postal_code && touched.postal_code && errors.postal_code}
              className="profile-personal-form__field"
            />
            <div className="profile-personal-form__group">
              <BasicUploadField
                name="proof_of_residential_address"
                label={t('profile_contacts_form.proof_of_residential_address')}
                value={values.proof_of_residential_address}
                acceptFormat={PDF_ONLY}
                multiple={false}
                isRequired
                tooltipContent={() => (
                  <div style={{ maxWidth: '200px' }}>
                    {t('profile_contacts_form.proof_of_residential_address_tooltip')}
                  </div>
                )}
                error={
                  errors.proof_of_residential_address &&
                  touched.proof_of_residential_address &&
                  errors.proof_of_residential_address
                }
                onError={message => setFieldError('proof_of_residential_address', message)}
                className="profile-startup-form__company_registry"
                onRemove={() => setFieldValue('proof_of_residential_address', [])}
                onChange={files => setFieldValue('proof_of_residential_address', files)}
              />
            </div>
          </div>

          <div className="profile-personal-form__buttons">
            {onCancel && (
              <RoundButton
                label={t('profile_contacts_form.cancel')}
                type="button"
                fullWidth
                onClick={onCancel}
                className="profile-personal-form__button"
              />
            )}
            <RoundButton
              label={t('profile_contacts_form.save')}
              type="submit"
              onSubmit={handleSubmit}
              fillBackground
              fullWidth
              className="profile-personal-form__button"
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default ProfileContactsForm;
